import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  Grid,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import JsonView from "react18-json-view";
import "react18-json-view/src/style.css";
import axiosInstance from "../../utils/axiosInstance";
import apiConfig from "../../config/appConfig";
import { format } from "date-fns";
import { DATE_FORMAT_AM_PM } from "../../utils/constants";

const RequestLogDataTable = () => {
  const [rows, setRows] = useState([]); // Request log data
  const [loading, setLoading] = useState(false); // Loading state
  const [totalRows, setTotalRows] = useState(0); // Total rows count
  const [searchText, setSearchText] = useState(""); // Search text
  const [paginationModel, setPaginationModel] = useState({
    page: 0, // DataGrid uses 0-based indexing
    pageSize: 10, // Default rows per page
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  }); // Snackbar state
  const [menuAnchorEl, setMenuAnchorEl] = useState(null); // Anchor for action menu
  const [menuRow, setMenuRow] = useState(null); // Selected row for menu actions
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [sortModel, setSortModel] = useState([
    { field: "executedAt", sort: "desc" },
  ]);

  // Fetch request logs from the backend
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        apiConfig.endpoints.requestLogs.paginated,
        {
          params: {
            page: paginationModel.page + 1, // Convert 0-based to 1-based indexing for backend
            limit: paginationModel.pageSize,
            search: searchText,
            sortField: sortModel[0]?.field || "executedAt",
            sortOrder: sortModel[0]?.sort || "desc",
          },
        }
      );

      const { requestLogs = [], totalPages = 0 } = response.data || {};
      setRows(requestLogs);
      setTotalRows(totalPages * paginationModel.pageSize); // Update total rows count
    } catch (error) {
      console.error("Error fetching RequestLogs:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch request logs.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [paginationModel, searchText]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleMenuOpen = (event, row) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuRow(null);
  };

  const handleView = (row) => {
    setViewData(row);
    setViewDialogOpen(true);
    handleMenuClose();
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSortModelChange = (model) => setSortModel(model);

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5 },
    { field: "uuid", headerName: "UUID", flex: 1.5 },
    
    { 
      headerName: "Customer Name", 
      flex: 1.5,
      renderCell: (params) => {
        if (!params.row.salesforceConfig) {
          return "None";
        }
        return params.row?.salesforceConfig?.customer.name || "None";
      },
    },
    { 
      field: "salesforceConfigName", 
      headerName: "Salesforce Config", 
      flex: 1.5,
      renderCell: (params) => {
        if (!params.row.salesforceConfig) {
          return "None";
        }
        return (params.row.salesforceConfig.name || "None") + " (" + (params.row.salesforceConfig.configType || "None") + ")";
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.row.status || "Unknown"}
          color={params.row.status === "success" ? "success" : "error"}
          size="small"
          sx={{
            fontWeight: "bold",
            textTransform: "capitalize",
            borderRadius: 1,
          }}
        />
      ),
    },
    { field: "errorMessage", headerName: "Error Message", flex: 2 },
    { 
      field: "executedAt", 
      headerName: "Executed At", 
      flex: 1.5,
      valueFormatter: (date) => {
        if (!date) return ""; // Handle null or undefined params
        try {
          return format(new Date(date), DATE_FORMAT_AM_PM); // Safely format the date
        } catch (error) {
          console.error("Invalid date format:", date);
          return "Invalid Date"; // Fallback for invalid dates
        }
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      renderCell: (params) => (
        <IconButton
          aria-label="more"
          onClick={(event) => handleMenuOpen(event, params.row)}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", padding: 2 }}>
      {/* Search and Title */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 2,
        }}
      >
        <Typography variant="h4">Request Logs</Typography>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchText}
          onChange={handleSearchChange}
          placeholder="Search by UUID or Status"
        />
      </Box>

      {/* DataGrid */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", height: 300 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            height: "70vh",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            boxShadow: "0 1px 5px rgba(0,0,0,0.1)",
          }}
        >
          <DataGrid
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 },
              },
            }}
            rows={rows}
            columns={columns}
            pagination
            paginationMode="server"
            pageSizeOptions={[10, 25, 50, 100]}
            rowsPerPageOptions={[10, 25, 50, 100]}
            paginationModel={paginationModel} 
            onPaginationModelChange={(newModel) => {
              console.log("Pagination model changed:", newModel); // Debugging
              setPaginationModel(newModel); // Update pagination state
            }}
            rowCount={totalRows}
            getRowId={(row) => row.id}
            disableSelectionOnClick
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}     
          />
        </Box>
      )}

      {/* Action Menu */}
      <Menu
        id="actions-menu"
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleView(menuRow)}>View</MenuItem>
      </Menu>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Request Log Details</DialogTitle>
        <DialogContent>
          {viewData && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">UUID</Typography>
                <Typography variant="body1">{viewData.uuid}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip
                  label={viewData.status || "Unknown"}
                  color={viewData.status === "success" ? "success" : "error"}
                  size="small"
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Executed At</Typography>
                <Typography variant="body1">{new Date(viewData.executedAt).toLocaleString()}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">HTTP Status Code</Typography>
                <Typography variant="body1">{viewData.httpStatusCode || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Response Time (ms)</Typography>
                <Typography variant="body1">{viewData.responseTime || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Method</Typography>
                <Typography variant="body1">{viewData.method || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Endpoint</Typography>
                <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>{viewData.endpoint || 'N/A'}</Typography>
              </Grid>
               <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">User Agent</Typography>
                <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>{viewData.userAgent || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">IP Address</Typography>
                <Typography variant="body1">{viewData.ipAddress || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Request Size (bytes)</Typography>
                <Typography variant="body1">{viewData.requestSize || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Response Size (bytes)</Typography>
                <Typography variant="body1">{viewData.responseSize || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Error Type</Typography>
                <Typography variant="body1">{viewData.errorType || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Error Message</Typography>
                {(() => {
                  try {
                    const parsedJson = JSON.parse(viewData.errorMessage);
                    return <JsonView src={parsedJson || {}} />;
                  } catch (error) {
                    return (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                        {viewData.errorMessage || 'N/A'}
                      </Typography>
                    );
                  }
                })()}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Request Payload</Typography>
                <JsonView src={viewData.requestPayload || {}} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Response Payload</Typography>
                <JsonView src={viewData.responsePayload || {}} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RequestLogDataTable;
